import * as THREE from 'three';

export interface EmitOptions {
    pos: THREE.Vector3;
    vel: THREE.Vector3;
    color: THREE.Color;
    /** World-space diameter. */
    size: number;
    life: number;
    /** Added to vy each second (negative = falls). */
    gravity?: number;
    /** 0..1 velocity kept per second, e.g. 0.2 loses 80% a second. */
    drag?: number;
    /** Shrink to nothing over the life instead of only fading. */
    shrink?: boolean;
}

const vertexShader = /* glsl */ `
    attribute float aSize;
    attribute float aAlpha;
    attribute vec3 aColor;
    uniform float uScale;
    varying float vAlpha;
    varying vec3 vColor;
    void main() {
        vAlpha = aAlpha;
        vColor = aColor;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = aSize * uScale / max(0.1, -mv.z);
        gl_Position = projectionMatrix * mv;
    }
`;

const fragmentShader = /* glsl */ `
    varying float vAlpha;
    varying vec3 vColor;
    void main() {
        float d = length(gl_PointCoord - 0.5) * 2.0;
        float a = smoothstep(1.0, 0.6, d) * vAlpha;
        if (a < 0.01) discard;
        gl_FragColor = vec4(vColor, a);
    }
`;

/** A pool of soft round sprites with per-particle size, colour, fade, gravity and drag. One draw call. */
export class Particles {
    readonly points: THREE.Points;
    private readonly max: number;
    private readonly pos: Float32Array;
    private readonly col: Float32Array;
    private readonly size: Float32Array;
    private readonly alpha: Float32Array;
    private readonly vel: Float32Array;
    private readonly life: Float32Array;
    private readonly maxLife: Float32Array;
    private readonly size0: Float32Array;
    private readonly grav: Float32Array;
    private readonly drag: Float32Array;
    private readonly shrink: Uint8Array;
    private cursor = 0;
    private readonly material: THREE.ShaderMaterial;
    private readonly geometry: THREE.BufferGeometry;

    constructor(max: number, blending: THREE.Blending) {
        this.max = max;
        this.pos = new Float32Array(max * 3);
        this.col = new Float32Array(max * 3);
        this.size = new Float32Array(max);
        this.alpha = new Float32Array(max);
        this.vel = new Float32Array(max * 3);
        this.life = new Float32Array(max);
        this.maxLife = new Float32Array(max);
        this.size0 = new Float32Array(max);
        this.grav = new Float32Array(max);
        this.drag = new Float32Array(max);
        this.shrink = new Uint8Array(max);

        this.geometry = new THREE.BufferGeometry();
        this.geometry.setAttribute(
            'position',
            new THREE.BufferAttribute(this.pos, 3)
        );
        this.geometry.setAttribute(
            'aColor',
            new THREE.BufferAttribute(this.col, 3)
        );
        this.geometry.setAttribute(
            'aSize',
            new THREE.BufferAttribute(this.size, 1)
        );
        this.geometry.setAttribute(
            'aAlpha',
            new THREE.BufferAttribute(this.alpha, 1)
        );

        this.material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            blending,
            uniforms: { uScale: { value: 500 } },
        });
        this.points = new THREE.Points(this.geometry, this.material);
        this.points.frustumCulled = false;
        this.points.renderOrder = 10;
    }

    /** Pixels per world unit at distance 1, from the viewport height and camera fov. */
    setScale(viewportHeightPx: number, fovDeg: number) {
        this.material.uniforms.uScale.value =
            viewportHeightPx /
            (2 * Math.tan(THREE.MathUtils.degToRad(fovDeg) / 2));
    }

    emit(o: EmitOptions) {
        const i = this.cursor;
        this.cursor = (this.cursor + 1) % this.max;
        this.pos[i * 3] = o.pos.x;
        this.pos[i * 3 + 1] = o.pos.y;
        this.pos[i * 3 + 2] = o.pos.z;
        this.vel[i * 3] = o.vel.x;
        this.vel[i * 3 + 1] = o.vel.y;
        this.vel[i * 3 + 2] = o.vel.z;
        this.col[i * 3] = o.color.r;
        this.col[i * 3 + 1] = o.color.g;
        this.col[i * 3 + 2] = o.color.b;
        this.size0[i] = o.size;
        this.size[i] = o.size;
        this.life[i] = o.life;
        this.maxLife[i] = o.life;
        this.grav[i] = o.gravity ?? 0;
        this.drag[i] = o.drag ?? 1;
        this.shrink[i] = o.shrink ? 1 : 0;
        this.alpha[i] = 1;
    }

    update(dt: number) {
        for (let i = 0; i < this.max; i++) {
            if (this.life[i] <= 0) {
                this.alpha[i] = 0;
                continue;
            }
            this.life[i] -= dt;
            const f = Math.max(0, this.life[i] / this.maxLife[i]);
            const k = Math.pow(this.drag[i], dt);
            this.vel[i * 3] *= k;
            this.vel[i * 3 + 1] = this.vel[i * 3 + 1] * k + this.grav[i] * dt;
            this.vel[i * 3 + 2] *= k;
            this.pos[i * 3] += this.vel[i * 3] * dt;
            this.pos[i * 3 + 1] += this.vel[i * 3 + 1] * dt;
            this.pos[i * 3 + 2] += this.vel[i * 3 + 2] * dt;
            this.alpha[i] = Math.min(1, f * 3);
            this.size[i] = this.shrink[i] ? this.size0[i] * f : this.size0[i];
        }
        (
            this.geometry.attributes.position as THREE.BufferAttribute
        ).needsUpdate = true;
        (this.geometry.attributes.aAlpha as THREE.BufferAttribute).needsUpdate =
            true;
        (this.geometry.attributes.aSize as THREE.BufferAttribute).needsUpdate =
            true;
        (this.geometry.attributes.aColor as THREE.BufferAttribute).needsUpdate =
            true;
    }

    clear() {
        this.life.fill(0);
        this.alpha.fill(0);
    }

    dispose() {
        this.geometry.dispose();
        this.material.dispose();
    }
}
